<div class="alert-settings-dialog p-4" data-testid="alert-settings-dialog">
  <form
    [formGroup]="alertForm"
    class="space-y-4"
    data-testid="alert-settings-dialog-form"
  >
    <!-- Scope -->
    <div class="w-full">
      <label
        for="alert-scope"
        class="block text-sm font-medium text-gray-700 mb-2"
      >
        {{ "alert_settings.form.scope" | translate }}
        <span class="text-red-500">*</span>
      </label>
      <div class="w-full" style="max-width: 100%">
        <p-select
          data-testid="alert-settings-dialog-scope-select"
          [options]="scopeOptions"
          formControlName="scope"
          inputId="alert-scope"
          optionLabel="label"
          optionValue="value"
          [placeholder]="'alert_settings.form.select_scope' | translate"
          class="w-full"
          [style]="{ width: '100%', maxWidth: '100%' }"
          panelStyleClass="alert-settings-scope-dropdown"
          [class.p-invalid]="showErrors() && alertForm.get('scope')?.invalid"
        >
          <ng-template let-scope pTemplate="item">
            <div class="flex flex-col p-1">
              <div class="text-sm font-semibold">{{ scope.label }}</div>
              <div class="text-xs">{{ scope.description }}</div>
            </div>
          </ng-template>
        </p-select>
      </div>
      @if (
        showErrors() &&
        alertForm.get("scope")?.invalid &&
        alertForm.get("scope")?.touched
      ) {
        <p class="text-red-500 text-xs mt-1">
          {{ "alert_settings.form.errors.scope_required" | translate }}
        </p>
      }
    </div>

    <!-- SKU Code -->
    <div class="w-full">
      <label
        for="alert-sku"
        class="block text-sm font-medium text-gray-700 mb-2"
      >
        {{ "alert_settings.form.sku" | translate }}
        <span class="text-red-500">*</span>
      </label>
      <div class="w-full" style="max-width: 100%">
        <app-paginated-select
          data-testid="alert-settings-dialog-sku-code-select"
          formControlName="skuCode"
          inputId="alert-sku"
          [paginatedOptions]="skuPaginatedConfig"
          [placeholder]="'alert_settings.search.select_sku' | translate"
          styleClass="w-full alert-settings-select"
          panelStyleClass="alert-settings-sku-dropdown"
          [invalid]="showErrors() && !!alertForm.get('skuCode')?.invalid"
        ></app-paginated-select>
      </div>
      @if (
        showErrors() &&
        alertForm.get("skuCode")?.invalid &&
        alertForm.get("skuCode")?.touched
      ) {
        <p class="text-red-500 text-xs mt-1">
          {{ "alert_settings.form.errors.sku_required" | translate }}
        </p>
      }
    </div>

    <!-- Warehouse -->
    <div class="w-full">
      <label
        for="alert-warehouse"
        class="block text-sm font-medium text-gray-700 mb-2"
      >
        {{ "alert_settings.form.warehouse" | translate }}
        <span class="text-red-500">*</span>
      </label>
      <div class="w-full" style="max-width: 100%">
        <app-paginated-select
          data-testid="alert-settings-dialog-warehouse-code-select"
          formControlName="warehouseCode"
          inputId="alert-warehouse"
          [paginatedOptions]="warehousePaginatedConfig"
          [placeholder]="'alert_settings.form.select_warehouse' | translate"
          styleClass="w-full alert-settings-select"
          panelStyleClass="alert-settings-warehouse-dropdown"
          [invalid]="
            showErrors() && !!alertForm.get('warehouseCode')?.invalid
          "
        ></app-paginated-select>
      </div>
      @if (
        showErrors() &&
        alertForm.get("warehouseCode")?.invalid &&
        alertForm.get("warehouseCode")?.touched
      ) {
        <p class="text-red-500 text-xs mt-1">
          {{ "alert_settings.form.errors.warehouse_required" | translate }}
        </p>
      }
    </div>

    <!-- Name -->
    <div>
      <label
        for="alert-name"
        class="block text-sm font-medium text-gray-700 mb-2"
      >
        {{ "alert_settings.form.name" | translate }}
      </label>
      <input
        pInputText
        data-testid="alert-settings-dialog-name-input"
        id="alert-name"
        formControlName="name"
        [placeholder]="'alert_settings.form.enter_name' | translate"
        class="w-full text-sm"
      />
    </div>

    <!-- Description -->
    <div>
      <label
        for="alert-description"
        class="block text-sm font-medium text-gray-700 mb-2"
      >
        {{ "alert_settings.form.description" | translate }}
      </label>
      <textarea
        pInputTextarea
        data-testid="alert-settings-dialog-description-textarea"
        id="alert-description"
        formControlName="description"
        [placeholder]="'alert_settings.form.enter_description' | translate"
        [rows]="3"
        class="w-full text-sm"
      ></textarea>
    </div>

    <!-- Min Stock -->
    <div>
      <label
        for="alert-min-stock"
        class="block text-sm font-medium text-gray-700 mb-2"
      >
        {{ "alert_settings.form.min_stock" | translate }}
      </label>
      <p-inputNumber
        data-testid="alert-settings-dialog-min-stock-input"
        formControlName="minStock"
        inputId="alert-min-stock"
        [placeholder]="'alert_settings.form.enter_min_stock' | translate"
        [min]="0"
        [useGrouping]="false"
        [showButtons]="false"
        class="w-full"
        [class.p-invalid]="showErrors() && alertForm.get('minStock')?.invalid"
      ></p-inputNumber>
      @if (
        showErrors() &&
        alertForm.get("minStock")?.invalid &&
        alertForm.get("minStock")?.touched
      ) {
        <p class="text-red-500 text-xs mt-1">
          @if (alertForm.get("minStock")?.errors?.["min"]) {
            {{ "alert_settings.form.errors.min_stock_min" | translate }}
          }
        </p>
      }
    </div>

    <!-- Max Stock -->
    <div>
      <label
        for="alert-max-stock"
        class="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
      >
        <span>
          {{ "alert_settings.form.max_stock" | translate }}
        </span>
        <i
          class="pi pi-info-circle text-blue-500 cursor-help"
          [pTooltip]="
            'alert_settings.form.errors.max_stock_invalid' | translate
          "
          tooltipPosition="top"
        ></i>
      </label>
      <p-inputNumber
        data-testid="alert-settings-dialog-max-stock-input"
        formControlName="maxStock"
        inputId="alert-max-stock"
        [placeholder]="'alert_settings.form.enter_max_stock' | translate"
        [min]="1"
        [useGrouping]="false"
        [showButtons]="false"
        styleClass="w-full"
        [class.p-invalid]="showErrors() && alertForm.get('maxStock')?.invalid"
      ></p-inputNumber>
      @if (
        showErrors() &&
        alertForm.get("maxStock")?.invalid &&
        alertForm.get("maxStock")?.touched
      ) {
        <p class="text-red-500 text-xs mt-1">
          @if (alertForm.get("maxStock")?.errors?.["min"]) {
            {{ "alert_settings.form.errors.max_stock_min" | translate }}
          }
        </p>
      }
      @if (showErrors() && alertForm.hasError("maxStockInvalid")) {
        <p class="text-red-500 text-xs mt-1">
          {{ "alert_settings.form.errors.max_stock_invalid" | translate }}
        </p>
      }
    </div>
  </form>

  <!-- Footer with error message and actions -->
  <div class="dialog-footer">
    <!-- Error message -->
    <div class="error-message-container">
      @if (errorMessage()) {
        <p-message
          severity="error"
          variant="simple"
          size="small"
          [text]="errorMessage() || undefined"
          styleClass="w-full"
        ></p-message>
      }
    </div>

    <!-- Actions -->
    <div class="flex justify-end gap-3">
      <app-button
        [testId]="'alert-settings-dialog-cancel-button'"
        [label]="'common.actions.cancel' | translate"
        type="secondary"
        [outlined]="true"
        (emitOnClick)="onCancel()"
      ></app-button>
      <app-button
        [testId]="'alert-settings-dialog-save-button'"
        [label]="'common.actions.save' | translate"
        type="info"
        [disabled]="saving()"
        (emitOnClick)="onSave()"
      ></app-button>
    </div>
  </div>
</div>
